import { AsyncPipe } from '@angular/common';
import { Observable } from 'rxjs';
import { Component, inject, input } from '@angular/core';
import { Platform } from '@angular/cdk/platform';

import { WINDOW_TOKEN } from '../../../services/window.token';
import { Breakpoint, BreakpointState, MediaQueryService } from '../../../services/media-query.service';
import { IconComponent } from '../../../components/icon/icon.component';
import { IconButtonBackground, IconButtonComponent } from '../../../components/icon-button/icon-button.component';
import { TranslatePipe } from '../../../pipes/translate.pipe';

@Component({
  selector: 'td-wb-toolbar-item-print',
  templateUrl: 'toolbar-item-print.component.html',
  imports: [
    IconComponent,
    TranslatePipe,
    IconButtonComponent,
    AsyncPipe,
    TranslatePipe
  ],
  host: {
    '[class.td-wb-toolbar-item__print]': 'toolbarPrintClass'
  }
})
export class ToolbarItemPrintComponent {
  private readonly browserWindow = inject<Window>(WINDOW_TOKEN);
  private readonly platform = inject(Platform);
  private readonly mediaQueryService = inject(MediaQueryService);

  toolbarPrintClass: boolean = true;
  readonly autoSizeOverride = input<boolean>(true);
  readonly tdWbIconButtonBackground = input<IconButtonBackground>('grey');

  breakpointChanges: Observable<BreakpointState>;

  constructor() {
    if (this.autoSizeOverride()) {
      this.breakpointChanges = this.mediaQueryService.observe(Breakpoint.LT_MD);
    }
  }

  print(): void {
    // Chrome applies @media print CSS rules before firing the `beforeprint` event,
    // but it may not immediately recompute content-driven heights for elements such
    // as the ngx-datatable scroller (which has no explicit height in non-virtual-scroll
    // mode). Without a forced layout flush, Chrome takes the print snapshot using
    // stale dimensions and only the first row is visible in the preview.
    //
    // Reading getBoundingClientRect() inside the `beforeprint` handler forces a
    // synchronous reflow with the print CSS applied so every row renders correctly.
    if (typeof this.browserWindow.addEventListener === 'function') {
      this.browserWindow.addEventListener('beforeprint', (): void => {
        const dt = this.browserWindow.document.querySelector('.ngx-datatable.td-wb-table');
        (dt ?? this.browserWindow.document.body).getBoundingClientRect();
      }, { once: true });
    }

    if (this.platform.SAFARI) {
      try {
        this.browserWindow.document.execCommand('print', false, null);
      } catch (e: unknown) {
        // eslint-disable-next-line no-console
        console.log(e);
        this.browserWindow.print(); // Future-proofing, if SAFARI decides to follow the consensus.
      }
    } else {
      this.browserWindow.print();
    }
  }
}
